import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import { useInScreen } from '@client/core/hooks/index.js';
import { i18n } from '@lingui/core';
import {
  type PlacementId,
  PlacementStatus
} from '@schibsted-nmp/advertising-shared';

import type { RetailMediaProduct } from '../types/index.js';
import {
  trackClickBackend,
  trackImpressionBackend
} from '../utils/RetailMediaApi.js';
import {
  trackImpressionPulse,
  trackClickPulse
} from '../utils/RetailMediaPulse.js';

export type SponsoredProductCarouselProps = {
  products: Array<RetailMediaProduct>;
  placementId: PlacementId;
  categories?: string[] | undefined;
};

/**
 * How many cards are visible at once. The carousel pages by a whole screenful,
 * so this also determines the number of pages.
 */
const BREAKPOINTS = [
  { query: '(min-width: 768px)', perPage: 3 },
  { query: '(min-width: 480px)', perPage: 2 }
] as const;

const NARROWEST_PER_PAGE = 1;

/** Locales BapSearchAd-free formatting can rely on; anything else falls back. */
const SUPPORTED_LOCALES = ['fi', 'nb', 'da', 'sv'];
const FALLBACK_LOCALE = 'nb';

function resolvePerPage(): number {
  if (typeof window === 'undefined') return NARROWEST_PER_PAGE;
  const match = BREAKPOINTS.find(
    ({ query }) => window.matchMedia(query).matches
  );
  return match?.perPage ?? NARROWEST_PER_PAGE;
}

function useCardsPerPage(): number {
  const [perPage, setPerPage] = useState(resolvePerPage);

  useEffect(() => {
    const queries = BREAKPOINTS.map(({ query }) => window.matchMedia(query));
    const onChange = () => {
      setPerPage(resolvePerPage());
    };
    queries.forEach((query) => {
      query.addEventListener('change', onChange);
    });
    return () => {
      queries.forEach((query) => {
        query.removeEventListener('change', onChange);
      });
    };
  }, []);

  return perPage;
}

function chunk<T>(items: Array<T>, size: number): Array<Array<T>> {
  const pages: Array<Array<T>> = [];
  for (let index = 0; index < items.length; index += size) {
    pages.push(items.slice(index, index + size));
  }
  return pages;
}

/**
 * Replaces the DOM-level decimal-separator workaround the single-product ad
 * needed: Intl already formats per locale, so `fi` gets its comma for free.
 */
function formatPrice(product: RetailMediaProduct, locale: string): string {
  return `${new Intl.NumberFormat(locale).format(product.price)} ${product.priceUnit}`;
}

function ChevronLeftIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M15 5.25 8.25 12 15 18.75"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M9 5.25 15.75 12 9 18.75"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DotsVerticalIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M8 4C7.31 4 6.75 3.44 6.75 2.75S7.31 1.5 8 1.5s1.25.56 1.25 1.25S8.69 4 8 4Z"
        fill="currentColor"
      />
      <path
        d="M8 9.25C7.31 9.25 6.75 8.69 6.75 8S7.31 6.75 8 6.75 9.25 7.31 9.25 8 8.69 9.25 8 9.25Z"
        fill="currentColor"
      />
      <path
        d="M8 14.5c-.69 0-1.25-.56-1.25-1.25S7.31 12 8 12s1.25.56 1.25 1.25S8.69 14.5 8 14.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg
      className="rm-card__external-icon"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M6 3H3V13H13V10M10 3H13M13 3V6M13 3L6 10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type CarouselCardProps = {
  product: RetailMediaProduct;
  locale: string;
  onImpression: (product: RetailMediaProduct) => void;
  onClick: (product: RetailMediaProduct) => void;
};

/**
 * One card owns its own viewability observer. Cards on later pages are clipped
 * by the viewport's `overflow: hidden`, so they stay unintersected until the
 * user actually pages to them - otherwise every product would burn an
 * impression on load.
 */
function CarouselCard({
  product,
  locale,
  onImpression,
  onClick
}: CarouselCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const hasTrackedView = useRef(false);

  const { hasIntersected } = useInScreen({
    ref: cardRef,
    threshold: 0.5,
    rootMargin: '0px'
  });

  useEffect(() => {
    if (!hasIntersected || hasTrackedView.current) return;
    hasTrackedView.current = true;
    onImpression(product);
  }, [hasIntersected, onImpression, product]);

  return (
    <div className="rm-card" ref={cardRef}>
      <a
        className="rm-card__link"
        href={product.productUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          onClick(product);
        }}
      >
        <div className="rm-card__image-wrap">
          <img
            className="rm-card__image"
            src={product.imageUrl}
            alt=""
            loading="lazy"
          />
        </div>
        <div className="rm-card__body">
          <span className="rm-card__price">{formatPrice(product, locale)}</span>
          <span className="rm-card__title">{product.title}</span>
          <span className="rm-card__advertiser">
            <span className="rm-card__advertiser-name">
              {product.advertiser}
            </span>
            <ExternalLinkIcon />
          </span>
        </div>
      </a>
      <a
        className="rm-card__dsa"
        href={product.dsaUrl}
        target="_blank"
        rel="noopener noreferrer"
        title="Ad info"
        aria-label="Ad info"
      >
        <DotsVerticalIcon />
      </a>
    </div>
  );
}

export function SponsoredProductCarousel({
  products,
  placementId,
  categories
}: SponsoredProductCarouselProps) {
  const perPage = useCardsPerPage();
  const [page, setPage] = useState(0);

  const pages = useMemo(() => chunk(products, perPage), [products, perPage]);
  const pageCount = Math.max(pages.length, 1);

  // A wider viewport means fewer pages - don't strand the user past the end.
  useEffect(() => {
    setPage((current) => Math.min(current, pageCount - 1));
  }, [pageCount]);

  const locale = SUPPORTED_LOCALES.includes(i18n.locale)
    ? i18n.locale
    : FALLBACK_LOCALE;

  const trackingContext = useMemo(
    () => ({
      zone: 'recommerce_search',
      categories,
      placementId
    }),
    [categories, placementId]
  );

  /**
   * Crossing a breakpoint re-chunks the products into differently keyed pages,
   * which remounts the cards that moved and resets their local
   * `hasTrackedView`. Impressions are billable, so dedupe by ad id here, where
   * the state survives a card remount.
   */
  const trackedImpressionIds = useRef(new Set<number>());

  const handleImpression = useCallback(
    (product: RetailMediaProduct) => {
      if (trackedImpressionIds.current.has(product.adId)) return;
      trackedImpressionIds.current.add(product.adId);

      updateMetricByKey(placementId, PlacementStatus.AdViewed);
      // Track server-side to avoid ad blockers
      void trackImpressionBackend(product.impressionUrl);
      trackImpressionPulse(product, trackingContext);
    },
    [placementId, trackingContext]
  );

  const handleClick = useCallback(
    (product: RetailMediaProduct) => {
      void trackClickBackend(product.clickUrl);
      trackClickPulse(product, trackingContext);
    },
    [trackingContext]
  );

  if (products.length === 0) return null;

  const advertisingLabel = products[0]?.advertisingLabel;

  return (
    <section
      className="rm-carousel"
      lang={locale}
      aria-roledescription="carousel"
      aria-label={advertisingLabel ?? 'Sponsored products'}
    >
      {advertisingLabel ? (
        <div className="rm-carousel__label">{advertisingLabel}</div>
      ) : null}

      <div className="rm-carousel__frame">
        <button
          type="button"
          className="rm-carousel__nav rm-carousel__nav--prev"
          onClick={() => {
            setPage((current) => Math.max(0, current - 1));
          }}
          disabled={page === 0}
          aria-label="Previous"
        >
          <ChevronLeftIcon />
        </button>

        <div className="rm-carousel__viewport">
          <div
            className="rm-carousel__track"
            style={{ transform: `translateX(-${page * 100}%)` }}
          >
            {pages.map((pageProducts, pageIndex) => (
              <div
                // Pages are a pure slice of `products`; index is the identity.
                key={`page-${pageIndex}`}
                className="rm-carousel__page"
                style={{ '--rm-per-page': perPage } as React.CSSProperties}
              >
                {pageProducts.map((product) => (
                  <CarouselCard
                    key={product.adId}
                    product={product}
                    locale={locale}
                    onImpression={handleImpression}
                    onClick={handleClick}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>

        <button
          type="button"
          className="rm-carousel__nav rm-carousel__nav--next"
          onClick={() => {
            setPage((current) => Math.min(pageCount - 1, current + 1));
          }}
          disabled={page >= pageCount - 1}
          aria-label="Next"
        >
          <ChevronRightIcon />
        </button>
      </div>
    </section>
  );
}
